/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

// Hàm mới để unsubscribe subscription hiện tại
const unsubscribeExisting = async registration => {
  try {
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      await existingSubscription.unsubscribe();
      console.log('Unsubscribed from existing push subscription');
    }
  } catch (error) {
    console.log('Error unsubscribing from existing subscription:', error);
  }
};

export const registerSubscription = async (onSuccess = () => {}) => {
  if (!window.hichatConfig.vapidPublicKey) {
    return;
  }

  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;

    // Kiểm tra subscription hiện tại
    const existingSubscription =
      await serviceWorkerRegistration.pushManager.getSubscription();

    // Nếu có subscription hiện tại, unsubscribe trước
    if (existingSubscription) {
      await unsubscribeExisting(serviceWorkerRegistration);
    }

    // Tạo subscription mới
    const subscription = await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: window.hichatConfig.vapidPublicKey,
    });

    await sendRegistrationToServer(subscription);
    onSuccess();
  } catch (err) {
    console.error('Push subscription error:', err);

    // Xử lý lỗi cụ thể về applicationServerKey
    if (err.message && err.message.includes('applicationServerKey')) {
      try {
        const serviceWorkerRegistration = await navigator.serviceWorker.ready;
        await unsubscribeExisting(serviceWorkerRegistration);

        // Thử lại sau khi unsubscribe
        const subscription = await serviceWorkerRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: window.hichatConfig.vapidPublicKey,
        });

        await sendRegistrationToServer(subscription);
        onSuccess();
      } catch (retryError) {
        console.error('Retry push subscription failed:', retryError);
        useAlert(
          `Push subscription error: ${retryError.message || 'Unknown error'}`
        );
      }
    } else {
      useAlert(`Push subscription error: ${err.message || 'Unknown error'}`);
    }
  }
};

export const requestPushPermissions = ({ onSuccess }) => {
  if (!('Notification' in window)) {
    useAlert('This browser does not support desktop notification');
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess);
      }
    });
  }
};

/**
 * Unsubscribe from push notifications (browser and server)
 * @param {Function} onSuccess - Callback when unsubscription succeeds
 * @param {Function} onError - Optional callback for error handling
 */
export const unsubscribePushNotification = async (onSuccess = () => {}, onError = null) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    const errorMsg = 'Browser does not support push notifications';
    if (onError) {
      onError(new Error(errorMsg));
    } else {
      useAlert(errorMsg);
    }
    return;
  }

  try {
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    const subscription = await serviceWorkerRegistration.pushManager.getSubscription();

    if (!subscription) {
      // No subscription exists, consider it already unsubscribed
      onSuccess();
      return;
    }

    const endpoint = subscription.endpoint;

    // Unsubscribe from browser first
    const unsubscribed = await subscription.unsubscribe();
    if (!unsubscribed) {
      throw new Error('Failed to unsubscribe from browser push service');
    }

    // Delete subscription from server
    if (auth.hasAuthCookie()) {
      try {
        await NotificationSubscriptions.delete({ endpoint });
      } catch (serverError) {
        // Log but don't fail if server deletion fails
        // Browser is already unsubscribed, which is the critical part
        console.warn('Failed to delete subscription from server:', serverError);
      }
    }

    onSuccess();
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    if (onError) {
      onError(error);
    } else {
      useAlert(`Failed to unsubscribe: ${error.message || 'Unknown error'}`);
    }
  }
};
